class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', this.onUpsellFormSubmit.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initUpsellSliders();
  }

  onUpsellFormSubmit(event) {
    const form = event.target;
    if (!form || !form.classList || !form.classList.contains('cart-drawer-upsell__form')) return;

    event.preventDefault();

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    const spinner = form.querySelector('.loading-overlay__spinner');

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    this.setActiveElement(document.activeElement);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    const errorsEl = this.querySelector('#CartDrawer-CartErrors');
    if (errorsEl) errorsEl.textContent = '';

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-drawer-upsell',
            productVariantId: formData.get('id'),
            errors: response.description,
            message: response.message
          });
          if (errorsEl) {
            const description = response.description;
            const msg = Array.isArray(description)
              ? description.join(', ')
              : description || response.message || '';
            errorsEl.textContent = msg;
          }
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upsell', productVariantId: formData.get('id') });
        if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
        if (errorsEl) errorsEl.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initUpsellSliders();
      this.open();
    });
  }

  initUpsellSliders() {
    const sliders = this.querySelectorAll('[data-upsell-slider]');
    if (!sliders.length) return;

    sliders.forEach((slider) => {
      if (slider.dataset.initialized === 'true') return;

      const track = slider.querySelector('.cart-drawer-upsell__track');
      const viewport = slider.querySelector('.cart-drawer-upsell__track-wrapper');
      if (!track || !viewport) return;

      const slides = Array.from(track.querySelectorAll('.cart-drawer-upsell__item'));
      const counter = slider.querySelector('[data-upsell-counter]');
      const prevButton = slider.querySelector('[data-upsell-scroll="prev"]');
      const nextButton = slider.querySelector('[data-upsell-scroll="next"]');
      if (!slides.length) return;

      const isRtl = () => getComputedStyle(slider).direction === 'rtl';

      const getCurrentSlide = () => {
        const viewportRect = viewport.getBoundingClientRect();
        const viewportEdge = isRtl() ? viewportRect.right : viewportRect.left;

        let currentIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const slideEdge = isRtl() ? rect.right : rect.left;
          const distance = Math.abs(slideEdge - viewportEdge);

          if (distance < bestDistance) {
            bestDistance = distance;
            currentIndex = index;
          }
        });

        return currentIndex + 1;
      };

      const updateCounter = () => {
        if (!counter) return;
        const current = getCurrentSlide();
        counter.textContent = `${current} / ${slides.length}`;
      };

      const scrollViewportToSlide = (index) => {
        const targetSlide = slides[index - 1];
        if (!targetSlide) return;

        targetSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: isRtl() ? 'end' : 'start'
        });
        setTimeout(updateCounter, 180);
      };

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          const current = getCurrentSlide();
          const prev = Math.max(1, current - 1);
          scrollViewportToSlide(prev);
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          const current = getCurrentSlide();
          const next = Math.min(slides.length, current + 1);
          scrollViewportToSlide(next);
        });
      }

      viewport.addEventListener('scroll', () => {
        window.requestAnimationFrame(updateCounter);
      }, { passive: true });

      updateCounter();
      slider.dataset.initialized = 'true';
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
